import React, { useEffect, useState } from "react";
import { SortDownArrow } from "../../assets/svg";
import Breadcrumb from "../../components/common/BreadCrumb";
import { ExportButton } from "../../components/micro/input";
import CustomTable from "../../components/customTable/CustomTable";
import { getOutstandingReportList } from "../../redux/reports/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { companiesList } from "../../redux/companies/slice";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";

const OutstandingReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [company, setCompany] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getOutstandingReportData, loading, companiesListData } = useSelector(
    (store) => ({
      getOutstandingReportData:
        store?.salesReportData?.getOutstandingReportListStatus?.data,
      loading: store?.salesReportData?.loading,
      companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
    })
  );

  useEffect(() => {
    dispatch(
      getOutstandingReportList({
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        date_range: DateRange || "",
        company: company?.label || "",
      })
    );
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
  }, [pageCount, pageLimit, sortBy, sortDirection, company, DateRange]);

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <span>No.</span>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("unique_id");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          Invoice Id
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "unique_id"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("invoice_date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Invoice Date</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "invoice_date"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("amount");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Price</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "amount" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
    </>
  );

  const DataRows = () => {
    return getOutstandingReportData?.data?.data?.map((item, index) => (
      <tr
        key={item?.id || index}
        className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
      >
        <td className="p-4 text-sm text-primaryBlack">{index + 1}</td>
        <td className="p-4 text-sm text-primaryBlack">
          {item?.company_name || "-"}
        </td>
        <td
          className="p-4 text-sm text-themeBtn underline cursor-pointer"
          onClick={() => navigate("/sales", { state: { tab: "Invoice" } })}
        >
          {item?.unique_id || "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {item?.invoice_date || "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          $
          {item?.amount !== null && item?.amount !== undefined
            ? Number(item.amount).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "-"}
        </td>
      </tr>
    ));
  };

  const companyOptions = (companiesListData?.data || companiesListData)?.map(
    (companies) => ({
      value: companies.id,
      label: companies.name,
    })
  );

  const handleCompanyChange = (selectedOption) => {
    setCompany(selectedOption);
  };

  const handleReportsExportClick = () => {
    const salesData = getOutstandingReportData?.data?.data;

    if (!salesData || salesData.length === 0) {
      alert("No sales data available to export!");
      return;
    }

    const header = ["No.", "Company Name", "Invoice ID", "Amount", "Date"];

    const rows = salesData.map((item, index) => [
      `"${index + 1}"`,
      `"${item?.company_name || "-"}"`,
      `"${item?.unique_id || "-"}"`,
      `"${item?.amount?.toLocaleString() || "-"}"`,
      `"${item?.invoice_date || "-"}"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "Outstanding-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Outstanding Reports
          </h6>
          <Breadcrumb
            breadCrumbParent="Home"
            breadCrumbParent2="Reports"
            breadCrumbActive="Outstanding Report"
            breadCrumbTitleKey="/"
            classes=""
          />
        </div>
      </div>
      <div className="mt-8">
        <div className="flex rounded-xl shadow-md px-6 py-4 w-1/3 bg-bgReport">
          <div className="w-1/3 border-stepGray">
            <p className="text-md font-normal">Total Unpaid:</p>
            <p className="text-xs text-textSecondary">Total Amount:</p>
            <p className="text-3xl mt-3 text-themeBtn font-Montserrat">
              $
              {getOutstandingReportData?.data?.total_unpaid_amount
                ? Number(
                    getOutstandingReportData.data.total_unpaid_amount
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "-"}
            </p>
          </div>
        </div>
      </div>
      {loading ? (
        <RippleLoader />
      ) : (
        <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
              Outstanding Report
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {/* Company Dropdown */}
              <div className="w-full sm:w-[200px] md:w-[220px] -mt-1 min-w-[160px]">
                <ReactSelect
                  className="w-full text-black p-0 font-Proxima"
                  id="company"
                  placeholder="Company"
                  options={companyOptions}
                  isClearable
                  getOptionValue={(option) => option?.value}
                  getOptionLabel={(option) => option?.label}
                  value={company}
                  onChange={handleCompanyChange}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: `1px solid #0000003B`,
                      borderColor: company ? "#F15B29" : "#0000003B",
                      borderRadius: "0.3rem",
                      boxShadow: "none",
                      "&:hover": {
                        border: "1px solid #0000003B",
                      },
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#F15B29" : "white",
                      "&:active": {
                        backgroundColor: state.isSelected
                          ? "#F15B29"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }),
                  }}
                />
              </div>

              {/* Date Range Picker */}
              <div className="w-full sm:w-[220px] md:w-[240px] -mt-1 min-w-[160px]">
                <div className="text-black border border-borderTheme/80 flex justify-between relative rounded w-full px-2 py-[7px] items-center transition duration-300 ease-in-out">
                  <ReactDatePicker
                    selected={dateRange[0]}
                    onChange={(dates) => setDateRange(dates)}
                    selectsRange
                    isClearable
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date Range"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Export Button */}
              <div className="w-full sm:w-auto -mt-1">
                <ExportButton
                  value="EXPORT CSV"
                  onClick={handleReportsExportClick}
                />
              </div>
            </div>
          </div>
          <CustomTable
            overflowVisibleLg={true}
            columnHeaders={<ColumnHeaders />}
            dataRows={<DataRows />}
            data={getOutstandingReportData?.data}
            loading={false}
            showPagination={true}
            setPaginationCurrentLimit={setPageLimit}
            paginationCurrentLimit={pageLimit}
            current={pageCount}
            total={getOutstandingReportData?.data?.total}
            paginationData={(crPage) => setPageCount(crPage)}
            lastPage={Math.ceil(
              getOutstandingReportData?.data?.total / pageLimit
            )}
          />
        </div>
      )}
    </div>
  );
};

export default OutstandingReports;
